const lightTheme = {
  name: 'light',
  colors: {
    // Legacy properties for backward compatibility
    primary: '#fa4616',
    primaryRgb: 'rgb(250, 70, 22)',
    primaryOpacity: (opacity) =>
      typeof opacity === 'number'
        ? `rgba(250, 70, 22, ${opacity})`
        : 'rgb(250, 70, 22)',
    secondary: '#00001f',
    black: '#000',
    blueBlack: '#000033',
    white: '#fff',
    ink: '31, 42, 48', // Design: Elevated --ink (RGB triplet for rgba composition)
    border: 'rgba(31, 42, 48, 0.1)', // Design: Elevated ink @ 10%
    'border-light': 'rgba(31, 42, 48, 0.07)', // Design: --line ink @ 7%
    'border-input': 'rgba(31, 42, 48, 0.12)', // Design: --field-bd
    errorBackground: '#FF9494',
    error: '#ED4337',
    success: '#22C55E', // Green used throughout app
    warning: '#FF9800',
    info: '#2196F3',

    // Background colors
    background: {
      primary: '#eaeef3', // Design: Elevated --page
      secondary: '#f2f2f2',
      tertiary: '#fafafa',
      light: '#F4F4F4',
      dark: '#00001f',
      mobile: '#00001f',
      card: '#ffffff', // Design: Elevated --card
      overlay: 'rgba(0, 0, 0, 0.03)',
    },

    // Uniform card styling for light theme
    card: {
      background: '#ffffff', // Design: Elevated --card (solid)
      border: 'rgba(31, 42, 48, 0.1)', // Design: Elevated ink @ 10%
      shadow: '0 8px 24px rgba(23, 37, 69, 0.12)', // Design: Elevated --shadow-card
      shadowHover: '0 18px 50px rgba(23, 37, 69, 0.22)', // Design: Elevated --shadow-pop
      headerBackground: '#fafbfd', // Design: Elevated --head (table header surface)
      inset: '#f3f5f8', // Design: Elevated --inset (fields / wells)
      halo: '0 0 0 2px rgba(250, 70, 22, 0.09)', // Design: Elevated --halo
      // Additional variations for consistency
      backgroundHover: '#ffffff',
      backgroundActive: '#f9fafb',
      borderHover: '#cbd5e1',
      borderActive: '#94a3b8',
    },

    // Uniform interactive element styling for light theme
    interactive: {
      // For subtle hover effects on rows/items
      hoverBackground: 'rgba(0, 0, 0, 0.02)',
      activeBackground: 'rgba(0, 0, 0, 0.04)',
      // For buttons
      buttonHoverBackground: '#f3f4f6',
      buttonActiveBackground: '#e5e7eb',
    },

    // Text colors
    text: {
      primary: '#1f2a30', // Design: Elevated --text
      secondary: '#647483', // Design: Elevated --sub
      dark: '#1f2a30', // Design: Elevated --text
      light: '#647483', // Design: Elevated --sub
      black: '#00001f',
      link: '#fa4616',
      white: '#ffffff',
      gray: '#647483', // Design: Elevated --sub
      header: '#1f2a30', // Design: Elevated --text
      muted: 'rgba(31, 42, 48, 0.42)', // Design: Elevated --faint
      label: '#64748b', // Design: Elevated --label (uppercase column labels)
    },

    // Gray scale
    gray: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#d8d8d8',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161',
    },

    // Analytics specific colors
    analytics: {
      cardBg: 'rgb(248, 250, 252)',
      cardBorder: 'rgba(0, 0, 0, 0.08)',
      cardShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      statValue: '#283339',
      statLabel: '#727279',
      chartPrimary: '#fa4616',
      chartSecondary: '#00001f',
      chartTertiary: '#4CAF50',
      chartQuaternary: '#2196F3',
      positive: '#22C55E', // Green for positive values
      negative: '#ED4337',
      neutral: '#9e9e9e',
    },

    // Segment colors (same for both light and dark themes)
    segments: {
      superFan: '#22C55E', // Green - Super Fan
      regularFan: '#3B82F6', // Blue - Regular Fan
      casualFan: '#06B6D4', // Cyan - Casual Fan
      firstTimer: '#F59E0B', // Orange - First-Timer
      inactive: '#DC2626', // Red - Inactive
    },

    // Status colors for toasters and alerts
    status: {
      success: '#22C55E', // Green - Same as superFan
      error: '#EF4444', // Red - Error primary
      errorAlt: '#DC2626', // Red - Error alt/gradient (same as inactive)
      info: '#3B82F6', // Blue - Same as regularFan
    },

    // Event status colors
    eventStatus: {
      draft: {
        background: 'rgba(100, 116, 139, 0.12)',
        text: '#64748b',
        border: 'rgba(100, 116, 139, 0.3)',
      },
      published: {
        background: 'rgba(34, 197, 94, 0.12)',
        text: '#16a34a',
        border: 'rgba(34, 197, 94, 0.3)',
      },
      postponed: {
        background: 'rgba(234, 179, 8, 0.12)',
        text: '#ca8a04',
        border: 'rgba(234, 179, 8, 0.3)',
      },
      cancelled: {
        background: 'rgba(239, 68, 68, 0.12)',
        text: '#dc2626',
        border: 'rgba(239, 68, 68, 0.3)',
      },
    },

    // Standardized gray text colors for light theme
    textGray: {
      primary: '#000033', // Same as blueBlack
      dark: '#334155', // Dark gray-blue for emphasis
      secondary: '#64748b', // Medium gray-blue for secondary text
      muted: '#94a3b8', // Light gray-blue for muted text
      disabled: '#9CA3AF', // Light gray for disabled states
    },
  },

  // Typography
  fonts: {
    primary:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    secondary:
      '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    monospace: '"SF Mono", Monaco, Consolas, "Courier New", monospace',
    // Required font definitions for Text component
    default: {
      fontSize: 14,
      lineHeight: 24,
      fontWeight: 400,
    },
    heading: {
      fontSize: 24,
      lineHeight: 28,
      fontWeight: 600,
      fontFamily: 'Barlow Condensed',
    },
    label: {
      fontSize: 16,
      lineHeight: 19,
      fontWeight: 500,
    },
    bold: {
      fontSize: 20,
      lineHeight: 24,
      fontWeight: 600,
    },
    body: {
      fontSize: 24,
      lineHeight: 28,
      fontWeight: 800,
    },
    body4: {
      fontSize: 14,
      lineHeight: 18,
      fontWeight: 400,
    },
  },

  // Spacing
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  // Border radius
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    round: '50%',
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },

  // Shadows
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12)',
    md: '0 2px 8px rgba(0, 0, 0, 0.08)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.1)',
    xl: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
};

export default lightTheme;
